import { Link } from "react-router-dom";
import { Star, ArrowUpRight } from "lucide-react";
import {
  JOURNAL_ENTRIES,
  OFFICES,
  CORE_PRACTICE_AREAS,
  TEXAS_METROS,
} from "@/lib/content";
import { BANKRUPTCY_PAGES, BANKRUPTCY_SLUGS } from "@/lib/bankruptcy";
import SEO from "@/components/SEO";
import { breadcrumbSchema } from "@/lib/schemas";

// ---------------------------------------------------------------------------
// Top-level navigation columns — read like a magazine's table of contents.
// ---------------------------------------------------------------------------
const NAV_COLUMNS = [
  {
    heading: "The Firm",
    items: [
      { label: "Home", to: "/" },
      { label: "Our Approach", to: "/about" },
      { label: "Attorneys — Darryl V. Pratt", to: "/attorneys/darryl-v-pratt" },
      { label: "The Journal", to: "/journal" },
      { label: "Contact", to: "/contact" },
      { label: "Frequently Asked Questions", to: "/faq" },
    ],
  },
  {
    heading: "Client Rails",
    items: [
      { label: "Request a Conference", to: "/intake" },
      { label: "For Advisors", to: "/for-advisors" },
      { label: "For Referring Counsel", to: "/for-counsel" },
      { label: "Client Portal — Sign In", to: "/login" },
      { label: "Locations Across Texas", to: "/locations" },
    ],
  },
  {
    heading: "The Fine Print",
    items: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Legal Disclosures", to: "/legal" },
      { label: "Terms of Use", to: "/terms" },
      { label: "Brand Kit", to: "/brand" },
    ],
  },
];

// ---------------------------------------------------------------------------
// Sub-component: hairline section header used across every block.
// ---------------------------------------------------------------------------
function SectionHeading({ kicker, title }) {
  return (
    <div className="mb-10 md:mb-14">
      <div className="flex items-baseline gap-3">
        <Star size={9} className="text-brass fill-brass" />
        <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
          {kicker}
        </div>
      </div>
      <h2 className="mt-5 font-display text-navy text-3xl md:text-4xl leading-[1.02]">
        {title}
      </h2>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: hairline directory row — the atomic unit of the sitemap.
// ---------------------------------------------------------------------------
function DirectoryRow({ to, label, meta, testId }) {
  return (
    <li className="hairline-top" data-testid={testId}>
      <Link
        to={to}
        className="group flex items-baseline justify-between gap-6 py-3.5 hover:bg-linen/50 transition-colors"
      >
        <span className="font-display text-navy text-[15px] md:text-base leading-snug group-hover:text-oxblood transition-colors">
          {label}
        </span>
        <span className="flex items-center gap-3 shrink-0">
          {meta && (
            <span className="hidden md:inline text-[10px] uppercase tracking-[0.28em] text-slate2/60">
              {meta}
            </span>
          )}
          <ArrowUpRight
            size={13}
            className="text-brass opacity-40 group-hover:opacity-100 transition-opacity"
          />
        </span>
      </Link>
    </li>
  );
}

export default function SiteMapPage() {
  return (
    <div className="bg-ivory" data-testid="site-map-page">
      <SEO
        title="Directory — Full Site Index · Lone Star Counsel"
        description="A complete editorial directory of Lone Star Counsel — every practice discipline, every essay, every Texas region we serve. Organised for readers and crawlers alike."
        path="/site-map"
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Site Map", path: "/site-map" },
          ]),
        ]}
      />

      {/* ================================================================
          HERO — restrained editorial mast.
      ================================================================= */}
      <section
        className="panel-navy grain grain-dark pt-40 pb-24 md:pt-48 md:pb-32 relative overflow-hidden"
        data-testid="sitemap-hero"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 relative">
          <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
            The Directory
          </div>
          <div className="mt-3 text-xs uppercase tracking-[0.28em] text-ivory/60">
            Site Map · Editorial Index
          </div>
          <h1
            className="mt-10 font-display text-5xl md:text-7xl lg:text-[7rem] leading-[0.94] text-ivory tracking-tight max-w-5xl"
            data-testid="sitemap-h1"
          >
            The firm,{" "}
            <span className="italic font-cormorant text-brass">
              in one directory.
            </span>
          </h1>
          <p className="mt-10 max-w-3xl text-lg md:text-xl text-ivory/85 font-light leading-relaxed">
            Every page, essay, discipline, and Texas region we serve — arranged
            here as an editorial index, in the order a reader would naturally
            move through them. Google finds this useful. So, occasionally, do we.
          </p>
        </div>
      </section>

      {/* ================================================================
          NAVIGATION — three columns.
      ================================================================= */}
      <section
        className="bg-linen grain py-20 md:py-28"
        data-testid="sitemap-navigation"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeading kicker="Navigation" title="What the site contains." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-14 gap-y-10">
            {NAV_COLUMNS.map((col) => (
              <div key={col.heading} data-testid={`nav-col-${col.heading.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium mb-4">
                  {col.heading}
                </div>
                <ul>
                  {col.items.map((item) => (
                    <DirectoryRow
                      key={item.to}
                      to={item.to}
                      label={item.label}
                      testId={`nav-item-${item.to.replace(/[^a-z]+/g, "-")}`}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          PRACTICE — the six pillars + featured subpages.
      ================================================================= */}
      <section
        className="bg-ivory grain py-24 md:py-32"
        data-testid="sitemap-practice"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeading
            kicker="Practice · Six Pillars"
            title="The full continuum of counsel."
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-14 gap-y-10">
            {/* Featured practice subpages */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium mb-4">
                Featured Subpages
              </div>
              <ul>
                <DirectoryRow
                  to="/practice"
                  label="All Services — the full continuum"
                  meta="Overview"
                  testId="practice-overview"
                />
                <DirectoryRow
                  to="/practice/private-client"
                  label="Private Client Services"
                  meta="By Referral"
                  testId="practice-private-client"
                />
                <DirectoryRow
                  to="/practice/intellectual-property"
                  label="Intellectual Property — Trademarks & Copyrights"
                  meta="Featured"
                  testId="practice-ip"
                />
                <DirectoryRow
                  to="/practice/creators-and-athletes"
                  label="Creators, Athletes & Founder-Creators"
                  meta="Emerging"
                  testId="practice-creators"
                />
              </ul>

              <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium mb-4 mt-10">
                Bankruptcy & Restructuring
              </div>
              <ul>
                {BANKRUPTCY_SLUGS.map((slug) => {
                  const page = BANKRUPTCY_PAGES[slug];
                  if (!page) return null;
                  return (
                    <DirectoryRow
                      key={slug}
                      to={`/practice/${slug}`}
                      label={page.serviceMeta?.name || page.headline}
                      meta={`${page.readMinutes || 8} min`}
                      testId={`bankruptcy-${slug}`}
                    />
                  );
                })}
              </ul>
            </div>

            {/* Core Practice Areas — from content.js */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium mb-4">
                Core Disciplines
              </div>
              <ul>
                {CORE_PRACTICE_AREAS.map((area, i) => (
                  <DirectoryRow
                    key={i}
                    to={area.href || "/practice"}
                    label={area.name || area.title || area}
                    meta={area.discipline}
                    testId={`core-discipline-${i}`}
                  />
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          THE JOURNAL — every essay on record.
      ================================================================= */}
      <section
        className="bg-linen grain py-24 md:py-32"
        data-testid="sitemap-journal"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeading
            kicker="The Journal · Complete Archive"
            title="Every essay on record."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-2">
            <ul>
              {JOURNAL_ENTRIES.slice(0, Math.ceil(JOURNAL_ENTRIES.length / 2)).map((entry) => (
                <DirectoryRow
                  key={entry.slug}
                  to={`/journal/${entry.slug}`}
                  label={entry.title}
                  meta={`${entry.no} · ${entry.minutes} min`}
                  testId={`journal-${entry.slug}`}
                />
              ))}
            </ul>
            <ul>
              {JOURNAL_ENTRIES.slice(Math.ceil(JOURNAL_ENTRIES.length / 2)).map((entry) => (
                <DirectoryRow
                  key={entry.slug}
                  to={`/journal/${entry.slug}`}
                  label={entry.title}
                  meta={`${entry.no} · ${entry.minutes} min`}
                  testId={`journal-${entry.slug}`}
                />
              ))}
            </ul>
          </div>
          <div className="mt-8">
            <Link
              to="/journal"
              className="text-[10px] uppercase tracking-[0.28em] text-oxblood hover:text-navy transition-colors font-medium border-b border-brass/60 hover:border-navy pb-1 inline-flex items-center gap-2"
              data-testid="sitemap-journal-all"
            >
              Browse the Journal <ArrowUpRight size={13} />
            </Link>
          </div>
        </div>
      </section>

      {/* ================================================================
          LOCATIONS — office regions + top-10 metros.
      ================================================================= */}
      <section
        className="bg-ivory grain py-24 md:py-32"
        data-testid="sitemap-locations"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeading
            kicker="Texas Coverage"
            title="Where the counsel is kept."
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-14 gap-y-10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium mb-4">
                Office Regions
              </div>
              <ul>
                {OFFICES.map((office) => (
                  <DirectoryRow
                    key={office.slug}
                    to={`/locations/${office.slug}`}
                    label={office.region}
                    meta={office.label}
                    testId={`office-${office.slug}`}
                  />
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium mb-4">
                Top Texas Metros (Editorial)
              </div>
              <ul>
                {TEXAS_METROS.map((m) => (
                  <DirectoryRow
                    key={m.slug}
                    to="/locations"
                    label={m.name}
                    meta={m.region}
                    testId={`metro-${m.slug}`}
                  />
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-10 max-w-3xl font-cormorant italic text-slate2 text-lg leading-snug">
            Beyond these ten metros, the firm serves 188+ Texas cities across all
            twelve regions of the state. If your matter is in Texas, we are within
            reach.
          </p>
        </div>
      </section>

      {/* ================================================================
          CLOSING NOTE — crawlable + human.
      ================================================================= */}
      <section
        className="panel-navy grain grain-dark py-20 md:py-24 text-center"
        data-testid="sitemap-closer"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Star size={11} className="text-brass fill-brass mx-auto" />
          <p className="mt-8 font-cormorant italic text-ivory/85 text-xl md:text-2xl leading-snug">
            The site map is regenerated when new essays are published or new
            disciplines are added. If you are searching for a particular piece
            of counsel and cannot find it here, write to us. The counsel table
            is open.
          </p>
          <div className="mt-10">
            <Link
              to="/intake"
              className="btn-brass inline-flex items-center gap-2"
              data-testid="sitemap-cta-intake"
            >
              <Star size={11} className="fill-current" />
              Request a Confidential Conference
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
